import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'
import { AuthUserButton } from '@/lib/auth'
import { filterNavGroups, type EnabledScreens } from '@/lib/nav-config'
import { enabledScreens } from '@/lib/runtime-config'

interface LayoutProps {
  children: React.ReactNode
  /**
   * WU-07: capability × deploy 設定を合成した実効画面セット。
   * 未指定時は WU-04 の deploy 単位 enabledScreens のみ（従来挙動）。
   */
  screens?: EnabledScreens
}

export function Layout({ children, screens }: LayoutProps) {
  const location = useLocation()
  const effectiveScreens = screens !== undefined ? screens : enabledScreens()
  // スマホではメニューを畳む。
  // 以前は上部に横スクロールする帯（実測 2,559px）が常時あり、縦に読もうとすると
  // 左右にズレて操作感が悪かった。畳めば横スクロールする要素自体が無くなる。
  const [navOpen, setNavOpen] = useState(false)
  useEffect(() => { setNavOpen(false) }, [location.pathname])

  return (
    // md:overflow-hidden で画面全体のスクロールを止め、
    // サイドバーと本文をそれぞれ独立にスクロールさせる（メニューが増えても崩れないようにする）
    <div className="flex min-h-[100dvh] w-full max-w-full flex-col bg-background md:h-[100dvh] md:flex-row md:overflow-hidden">
      {/* Sidebar */}
      <aside
        data-app-chrome
        className="w-full min-w-0 max-w-full shrink-0 border-b bg-card md:flex md:h-full md:w-64 md:flex-col md:border-b-0 md:border-r"
      >
        <div className="flex h-14 items-center justify-between gap-2 border-b px-4 md:h-16 md:shrink-0 md:px-6">
          <h1 className="text-xl font-bold text-primary">Context Harness</h1>
          <button
            type="button"
            onClick={() => setNavOpen((v) => !v)}
            aria-expanded={navOpen}
            aria-controls="app-nav"
            className="-mr-2 inline-flex min-h-10 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden"
          >
            {navOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
            メニュー
          </button>
        </div>
        {/* スマホは畳む（横スクロールさせない）。PC はメニューが増えたらここだけがスクロールする */}
        <nav
          id="app-nav"
          aria-label="主要ナビゲーション"
          className={cn(
            'min-w-0 max-w-full p-2 md:block md:min-h-0 md:flex-1 md:overflow-y-auto md:p-4',
            navOpen ? 'block border-t' : 'hidden',
          )}
        >
          <div className="flex flex-col gap-4 md:block md:w-auto md:space-y-6">
            {filterNavGroups(effectiveScreens).map((group) => (
              <div key={group.label} className="md:space-y-1">
                <p className="px-3 pb-1 text-xs font-semibold text-muted-foreground">
                  {group.label}
                </p>
                {group.items.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.screen}
                      to={item.href}
                      className={cn(
                        'flex min-h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:gap-3',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      <item.icon className="h-5 w-5" />
                      {item.name}
                    </Link>
                  )
                })}
              </div>
            ))}
          </div>
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header */}
        <header
          data-app-chrome
          // canvas をグレーにしたのでヘッダは白で分離
          className="flex h-14 shrink-0 items-center justify-end border-b bg-card px-4 md:h-16 md:px-6"
        >
          <AuthUserButton />
        </header>

        {/* Page content */}
        <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-6">
          {/* 1 画面の不具合でサイドバーごと真っ白にしない（他画面へ戻れるようにする） */}
          <RouteErrorBoundary resetKey={location.pathname}>
            {children}
          </RouteErrorBoundary>
        </main>
      </div>
    </div>
  )
}
